import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MessageSquare, X, Send, Loader2, CheckCircle2, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_CHARS = 160;

const STRINGS = {
  en: {
    button: 'Text Us',
    title: 'Text Us',
    yourName: 'Your Name',
    namePh: 'First & Last Name',
    interested: "I'm interested in:",
    groupServices: 'Services',
    groupIndustries: 'Industries',
    groupProducts: 'Products',
    messageOpt: 'Message (optional)',
    messagePh: 'Add a short message...',
    charCount: '{count}/{max} chars',
    overLimit: 'Over limit',
    yourPhone: 'Your Phone Number',
    phonePh: '(555) 123-4567',
    preview: 'Preview:',
    openMessages: 'Open Messages App',
    sendMessage: 'Send Message',
    messageSent: 'Message Sent!',
    wellGetBack: "We'll get back to you shortly.",
    close: 'Close',
  },
  es: {
    button: 'Envíanos un Mensaje',
    title: 'Envíanos un Mensaje',
    yourName: 'Tu Nombre',
    namePh: 'Nombre y Apellido',
    interested: 'Me interesa:',
    groupServices: 'Servicios',
    groupIndustries: 'Industrias',
    groupProducts: 'Productos',
    messageOpt: 'Mensaje (opcional)',
    messagePh: 'Agrega un mensaje corto...',
    charCount: '{count}/{max} caracteres',
    overLimit: 'Límite excedido',
    yourPhone: 'Tu Número de Teléfono',
    phonePh: '(555) 123-4567',
    preview: 'Vista previa:',
    openMessages: 'Abrir App de Mensajes',
    sendMessage: 'Enviar Mensaje',
    messageSent: '¡Mensaje Enviado!',
    wellGetBack: 'Te responderemos pronto.',
    close: 'Cerrar',
  },
};

const INTERESTS = [
  { key: 'webDesign', labelEn: 'Web Design', labelEs: 'Diseño Web', group: 'Services' },
  { key: 'seo', labelEn: 'SEO Optimization', labelEs: 'Optimización SEO', group: 'Services' },
  { key: 'social', labelEn: 'Social Media Management', labelEs: 'Gestión de Redes Sociales', group: 'Services' },
  { key: 'ppc', labelEn: 'PPC / Google Ads', labelEs: 'PPC / Google Ads', group: 'Services' },
  { key: 'custom', labelEn: 'Custom Development', labelEs: 'Desarrollo a Medida', group: 'Services' },
  { key: 'consulting', labelEn: 'Consulting', labelEs: 'Consultoría', group: 'Services' },
  { key: 'dealers', labelEn: 'Car & RV Dealers', labelEs: 'Concesionarios de Autos y RV', group: 'Industries' },
  { key: 'localBiz', labelEn: 'Local Business', labelEs: 'Negocio Local', group: 'Industries' },
  { key: 'enterprise', labelEn: 'Enterprise', labelEs: 'Empresa', group: 'Industries' },
  { key: 'mobileApps', labelEn: 'Mobile Apps', labelEs: 'Apps Móviles', group: 'Products' },
  { key: 'aiVisibility', labelEn: 'AI Visibility', labelEs: 'Visibilidad en IA', group: 'Products' },
  { key: 'turboSeo', labelEn: 'Turbo SEO', labelEs: 'Turbo SEO', group: 'Products' },
  { key: 'portfolio', labelEn: 'Portfolio Website', labelEs: 'Sitio de Portafolio', group: 'Products' },
];

export default function TextUsWidget() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en';
  const s = STRINGS[lang];
  const groupLabels = {
    Services: s.groupServices,
    Industries: s.groupIndustries,
    Products: s.groupProducts,
  };
  const interestLabel = (item) => (lang === 'es' ? item.labelEs : item.labelEn);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [isMobile] = useState(() => typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  const [mainNumber, setMainNumber] = useState('8173205334');
  const panelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/sms/text-us-config`);
        const num = (data.number || '').replace(/\D/g, '').replace(/^1/, '');
        if (!cancelled && num) setMainNumber(num);
      } catch { /* keep default number */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Allow the mobile header "Text Us" button to open this widget.
  useEffect(() => {
    const openWidget = () => setOpen(true);
    window.addEventListener('wts:open-textus', openWidget);
    return () => window.removeEventListener('wts:open-textus', openWidget);
  }, []);

  const toggleInterest = (key) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(i => i !== key) : [...prev, key]
    );
  };

  const composedMessage = () => {
    const parts = [];
    if (name.trim()) {
      parts.push(name.trim());
    }
    if (selected.length > 0) {
      // Always transmit English labels in outgoing SMS so ops team reads one language.
      const labels = selected
        .map(k => INTERESTS.find(i => i.key === k))
        .filter(Boolean)
        .map(i => i.labelEn);
      parts.push(labels.join(', '));
    }
    if (message.trim()) {
      parts.push(message.trim());
    }
    return parts.join(' | ') || '';
  };

  const totalChars = composedMessage().length;
  const remaining = MAX_CHARS - totalChars;

  const displayNumber = `(${mainNumber.slice(0,3)}) ${mainNumber.slice(3,6)}-${mainNumber.slice(6)}`;

  const handleMobileSend = () => {
    const body = encodeURIComponent(composedMessage());
    window.location.href = `sms:+1${mainNumber}?body=${body}`;
  };

  const handleDesktopSend = async () => {
    if (!phone || totalChars === 0) return;
    setSending(true);
    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const formattedPhone = cleanPhone.startsWith('1') ? `+${cleanPhone}` : `+1${cleanPhone}`;
      const interestLabelsEn = selected
        .map(k => INTERESTS.find(i => i.key === k))
        .filter(Boolean)
        .map(i => i.labelEn);
      await axios.post(`${API}/api/sms/text-us`, {
        visitor_phone: formattedPhone,
        name: name.trim(),
        message: message.trim(),
        interests: interestLabelsEn,
        preferred_language: lang,
      });
      setSent(true);
    } catch (e) {
      console.error(e);
    }
    setSending(false);
  };

  const reset = () => {
    setSelected([]);
    setMessage('');
    setName('');
    setPhone('');
    setSent(false);
    setOpen(false);
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="text-us-btn"
          aria-label={s.button}
          className="flex fixed right-0 top-1/2 -translate-y-1/2 z-50 flex-col items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-1.5 py-3 rounded-l-lg shadow-lg transition-all hover:px-2.5"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-sm font-semibold tracking-wide" style={{ writingMode: 'vertical-rl' }}>{s.button}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          className="fixed right-4 bottom-4 z-50 w-80 max-h-[85vh] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ animation: 'slideUp 0.25s ease-out' }}
          data-testid="text-us-panel"
        >
          {/* Header */}
          <div className="bg-blue-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              <p className="text-white font-semibold text-sm">{s.title}</p>
              <a
                href={`tel:+1${mainNumber}`}
                data-testid="text-us-call-link"
                className="text-blue-100 text-xs inline-flex items-center gap-1 hover:text-white hover:underline transition-colors"
              >
                <Phone className="h-3 w-3" /> {displayNumber}
              </a>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          {sent ? (
            /* Success State */
            <div className="p-6 text-center flex-1 flex flex-col items-center justify-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
              <p className="text-gray-800 font-semibold mb-1">{s.messageSent}</p>
              <p className="text-gray-500 text-sm mb-4">{s.wellGetBack}</p>
              <button onClick={reset} className="text-blue-600 text-sm hover:underline">{s.close}</button>
            </div>
          ) : (
            /* Form */
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {/* Name */}
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">{s.yourName}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    const next = composedMessage().length - name.length + e.target.value.length;
                    if (next <= MAX_CHARS) setName(e.target.value);
                  }}
                  placeholder={s.namePh}
                  data-testid="text-us-name"
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Interests grouped */}
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1.5">{s.interested}</label>
                {['Services', 'Industries', 'Products'].map(group => {
                  const items = INTERESTS.filter(i => i.group === group);
                  return (
                    <div key={group} className="mb-2">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{groupLabels[group]}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map(item => (
                          <button
                            key={item.key}
                            onClick={() => toggleInterest(item.key)}
                            data-testid={`interest-${item.key}`}
                            className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${
                              selected.includes(item.key)
                                ? 'bg-blue-600 border-blue-500 text-white'
                                : 'bg-gray-50 border-gray-300 text-gray-600 hover:border-blue-400'
                            }`}
                          >
                            {interestLabel(item)}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message */}
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">{s.messageOpt}</label>
                <textarea
                  value={message}
                  onChange={(e) => {
                    const next = composedMessage().length - message.length + e.target.value.length;
                    if (next <= MAX_CHARS) setMessage(e.target.value);
                  }}
                  placeholder={s.messagePh}
                  rows={2}
                  data-testid="text-us-message"
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:border-blue-500"
                />
                <div className="flex justify-between mt-0.5">
                  <span className={`text-xs ${remaining < 20 ? 'text-amber-500' : remaining < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                    {s.charCount.replace('{count}', totalChars).replace('{max}', MAX_CHARS)}
                  </span>
                  {remaining < 0 && <span className="text-xs text-red-500">{s.overLimit}</span>}
                </div>
              </div>

              {/* Phone Number (desktop only) */}
              {!isMobile && (
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">{s.yourPhone}</label>
                  <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
                    <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={s.phonePh}
                      data-testid="text-us-phone"
                      className="bg-transparent text-sm text-gray-800 placeholder-gray-400 w-full focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Preview */}
              {totalChars > 0 && (
                <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-100">
                  <p className="text-xs text-gray-500 mb-1">{s.preview}</p>
                  <p className="text-xs text-gray-700 break-words">{composedMessage()}</p>
                </div>
              )}
            </div>
          )}

          {/* Send Button */}
          {!sent && (
            <div className="p-3 border-t border-gray-200 flex-shrink-0">
              {isMobile ? (
                <button
                  onClick={handleMobileSend}
                  disabled={totalChars === 0 || remaining < 0}
                  data-testid="text-us-send"
                  className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Send className="h-4 w-4" /> {s.openMessages}
                </button>
              ) : (
                <button
                  onClick={handleDesktopSend}
                  disabled={sending || totalChars === 0 || remaining < 0 || !phone}
                  data-testid="text-us-send"
                  className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {s.sendMessage}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}
